import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Radio, Megaphone, Rocket, ExternalLink, Clock, Play, Youtube, Share2, Download, Image, X, ZoomIn, Music, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useRadio } from "@/context/RadioContext";
import LeybaProLogo from "@/components/LeybaPRoLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Admin WhatsApp number for contact
const ADMIN_WHATSAPP = "18099788796";

// Promo Slide Card Component
const PromoSlideCard = ({ slide }) => {
  const IconComponent = {
    rocket: Rocket,
    youtube: Youtube,
    share: Share2,
    music: Music,
    users: Users,
    radio: Radio
  }[slide.icon] || Rocket;

  // Use different colors based on slide type - rocket/youtube get reddish colors
  const isVideoPromo = slide.icon === 'rocket' || slide.icon === 'youtube';
  const platformGradient = isVideoPromo 
    ? "from-[#2a1a1a] via-[#3d1f1f] to-[#1a0f0f]" // Reddish dark
    : "from-[#1a1a2e] via-[#16213e] to-[#0f0f23]"; // Bluish dark
  const accentColor = isVideoPromo ? "#FF6B6B" : "#FFD700";
  const iconBgGradient = isVideoPromo 
    ? "from-[#FF6B6B] to-[#DC2626]" // Red gradient
    : "from-[#FFD700] to-[#B8860B]"; // Gold gradient

  return (
    <Card className={`overflow-hidden border border-[${accentColor}]/20 bg-gradient-to-br ${platformGradient} hover:border-[${accentColor}]/40 hover:scale-[1.01] transition-all duration-300`}>
      <CardContent className="p-5 text-center relative">
        <div className={`absolute inset-0 bg-gradient-to-t from-[${accentColor}]/5 to-transparent`} />
        <div className="relative z-10">
          <div className={`w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br ${iconBgGradient} flex items-center justify-center`}>
            <IconComponent className={`w-7 h-7 ${isVideoPromo ? 'text-white' : 'text-black'}`} />
          </div>
          <h3 className={`text-lg font-bold mb-1`} style={{color: accentColor}}>{slide.title}</h3>
          <p className="text-base font-medium text-white/90 mb-2">{slide.subtitle}</p>
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">{slide.description}</p>
          <a 
            href={`https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent(`Hola! Me interesa: ${slide.title}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#25D366]/90 text-white font-bold px-5 py-2 text-sm rounded-full shadow-lg transition-all"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Contactar
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { currentTrack, isPlaying, togglePlay, needsUserInteraction } = useRadio();
  const [artists, setArtists] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [promotedLaunches, setPromotedLaunches] = useState([]);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [galleryArtworks, setGalleryArtworks] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [launchingSoon, setLaunchingSoon] = useState(null);
  const [launchAlert, setLaunchAlert] = useState(null);
  const [hasVibrated, setHasVibrated] = useState({});
  
  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);

  // Check for upcoming launches every 30 seconds
  useEffect(() => {
    const checkLaunchingSoon = async () => {
      try {
        const response = await axios.get(`${API}/launch-events/starting-soon`);
        if (response.data.has_upcoming_launch) {
          setLaunchingSoon(response.data);
          
          // Check if it's launch time (less than 1 minute)
          if (response.data.minutes_until_launch <= 1 && response.data.launch?.id) {
            const launchId = response.data.launch.id;
            
            // Only vibrate and alert once per launch
            if (!hasVibrated[launchId]) {
              // Vibrate if supported
              if (navigator.vibrate) {
                navigator.vibrate([200, 100, 200, 100, 200]);
              }
              
              // Show alert
              setLaunchAlert({
                title: response.data.launch.title,
                artist: response.data.launch.artist_name,
                url: response.data.launch.youtube_url
              });
              
              // Mark as vibrated
              setHasVibrated(prev => ({ ...prev, [launchId]: true }));
              
              // Auto-hide alert after 10 seconds
              setTimeout(() => {
                setLaunchAlert(null);
              }, 10000);
            }
          }
        } else {
          setLaunchingSoon(null);
        }
      } catch (error) {
        console.log("Error checking launches:", error);
      }
    };
    
    checkLaunchingSoon();
    const interval = setInterval(checkLaunchingSoon, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  // PWA Install logic
  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      setShowInstallInstructions(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    // If we have the deferred prompt, use it (Chrome/Edge)
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
      }
      setDeferredPrompt(null);
    } else {
      // Show manual instructions for Safari/iOS and other browsers
      setShowInstallInstructions(true);
    }
  };

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  // Carousel only for artists and launches (not promos)
  // Get artist slides from carousel (added by admin)
  const artistSlidesFromCarousel = carouselSlides
    .filter(s => s.slide_type === 'artist' && s.is_active)
    .map(s => ({
      ...s,
      type: 'carousel-artist',
      name: s.title,
      bio: s.subtitle,
      photo_url: s.image_url,
      next_launch_datetime: s.launch_datetime
    }));
  
  // Get promo slides (non-artist type)
  const promoSlides = carouselSlides.filter(s => s.slide_type !== 'artist' && s.is_active);
  
  // Carousel items: ONLY admin-added artist slides + promoted launches.
  // Artist profile photos are NOT auto-added anymore — only the admin can add them via carousel slides.
  const carouselItems = [
    ...artistSlidesFromCarousel,
    ...promotedLaunches.map(p => ({ ...p, type: 'launch' }))
  ];
  
  // Split promo slides: 1 for after carousel, 2 for after gallery
  const promoSlidesTop = promoSlides.slice(0, 1);
  const promoSlidesBottom = promoSlides.slice(1, 3);
  
  // Format launch date/time
  const formatLaunchDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    if (date < now) return null; // Past launches
    
    // Format: "Sáb 15 Feb - 8:00 PM"
    const dayName = date.toLocaleDateString('es-DO', { weekday: 'short' });
    const dayNum = date.getDate();
    const month = date.toLocaleDateString('es-DO', { month: 'short' });
    const time = date.toLocaleTimeString('es-DO', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${dayName} ${dayNum} ${month} - ${time}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artistsRes, announcementsRes, promotionsRes, carouselRes, galleryRes] = await Promise.all([
          axios.get(`${API}/artists`),
          axios.get(`${API}/announcements`),
          axios.get(`${API}/launch-promotions/active`).catch(() => ({ data: [] })),
          axios.get(`${API}/carousel/slides`).catch(() => ({ data: [] })),
          axios.get(`${API}/gallery/artworks`).catch(() => ({ data: [] }))
        ]);
        setArtists(artistsRes.data);
        setAnnouncements(announcementsRes.data);
        setPromotedLaunches(promotionsRes.data);
        setCarouselSlides(carouselRes.data);
        setGalleryArtworks(galleryRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Auto-slide every 8 seconds
  useEffect(() => {
    if (carouselItems.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [carouselItems.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.max(carouselItems.length, 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.max(carouselItems.length, 1)) % Math.max(carouselItems.length, 1));
  };

  const defaultImages = [
    "https://images.unsplash.com/photo-1533609673390-a23103652dee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    "https://images.unsplash.com/photo-1586712447133-1d8836ca525c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    "https://images.pexels.com/photos/2204724/pexels-photo-2204724.jpeg?w=800"
  ];

  const getYouTubeId = (url) => {
    if (!url) return null;
    if (url.includes("youtube.com/watch?v=")) {
      return url.split("v=")[1]?.split("&")[0];
    } else if (url.includes("youtu.be/")) {
      return url.split("youtu.be/")[1]?.split("?")[0];
    }
    return null;
  };

  return (
    <div className="min-h-screen pb-20 animate-fadeInUp bg-gradient-to-b from-[#0a0a0a] to-[#050505]" data-testid="home-page">
      {/* Fixed Radio at Top - Pink and Gold theme */}
      <div className="sticky top-0 z-50 bg-[#0a0a0a] border-b border-white/10" data-testid="radio-section">
        <div className="max-w-md mx-auto p-3">
          <div className="flex items-center justify-between bg-[#121212] rounded-xl p-3 border border-white/10">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg transition-all duration-300 ${isPlaying ? "bg-gradient-to-br from-[#D946EF] to-[#FFD700]" : "bg-[#1E1E1E]"}`}>
                <Radio className={`w-5 h-5 ${isPlaying ? "text-white" : "text-[#D946EF]"}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-sm text-white">Radio LEYBA</h3>
                  {isPlaying && <span className="px-2 py-0.5 text-[8px] bg-gradient-to-r from-[#D946EF] to-[#FFD700] text-white rounded-full font-bold">EN VIVO</span>}
                </div>
                {currentTrack ? (
                  <p className="text-xs text-gray-400 truncate max-w-[180px]">
                    {currentTrack.title} - {currentTrack.artist_name}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">Música urbana 24/7</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isInstalled && (
                <button
                  onClick={handleInstallClick}
                  data-testid="pwa-install-btn-top"
                  title="Descargar App"
                  className="flex items-center gap-1.5 bg-gradient-to-br from-[#FFD700] to-[#B8860B] text-black font-bold text-xs px-3 h-10 rounded-full shadow-lg shadow-[#FFD700]/40 hover:scale-105 active:scale-95 transition-transform duration-300"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar</span>
                </button>
              )}
              <Button
                onClick={togglePlay}
                data-testid="radio-play-btn"
                className={`rounded-full w-10 h-10 transition-all duration-300 ${
                  isPlaying 
                    ? "bg-gradient-to-br from-[#D946EF] to-[#FFD700] hover:opacity-80" 
                    : "bg-[#1E1E1E] hover:bg-[#2a2a2a] border border-white/10"
                }`}
              >
                {isPlaying ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-[#D946EF]" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* LEYBA PRO Entertainment - Profile Style Banner */}
      <div className="px-4 py-3 bg-[#050505]">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-br from-[#2D1B4E] via-[#1E1233] to-[#2D1B4E] rounded-2xl p-4 border border-[#D946EF]/30 shadow-lg shadow-[#D946EF]/10">
            <div className="flex items-center gap-4">
              {/* Logo circular con borde rosa */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-full border-2 border-[#D946EF] p-1 bg-gradient-to-br from-[#D946EF]/20 to-transparent">
                  <div className="w-full h-full rounded-full overflow-hidden bg-black flex items-center justify-center">
                    <img 
                      src={`${BACKEND_URL}/api/uploads/gallery/gallery_9d5412d7-d6b7-4baa-ac69-cc7d2377667a.jpg`}
                      alt="LEYBA PRO"
                      className="w-full h-full object-cover"
                      onError={(e) => { 
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span class="text-[#FFD700] text-xs font-bold">LEYBA PRO</span>';
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Texto */}
              <div className="flex-1">
                <h3 className="text-white text-xl font-black tracking-wide">LEYBA PRO</h3>
                <p className="text-[#D946EF] text-sm font-medium tracking-wider">ENTERTAINMENT</p>
                <p className="text-gray-400 text-xs mt-1 italic">Donde el talento brilla</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Carousel - Same width as other panels */}
      <section className="relative overflow-hidden px-4 py-6" data-testid="artist-carousel">
        <div className="max-w-md mx-auto">
          <div className="relative h-[45vh] min-h-[300px] rounded-xl overflow-hidden border border-[#FFD700]/20">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0a] z-10 pointer-events-none" />
            
            {carouselItems.length > 0 ? (
              <div 
                className="flex transition-transform duration-700 ease-in-out h-full"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
            {carouselItems.map((item, index) => (
              <div key={item.id} className="min-w-full h-full relative">
                {item.type === 'launch' ? (
                  // Promoted Launch Slide with Date/Time
                  <>
                    <img
                      src={item.thumbnail_url || `https://img.youtube.com/vi/${getYouTubeId(item.youtube_url)}/maxresdefault.jpg`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = defaultImages[0]; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="bg-[#FF6B6B] text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-pulse">
                          <Rocket className="w-3 h-3" />
                          LANZAMIENTO
                        </span>
                        {/* Date/Time Badge */}
                        <span className="bg-[#FFD700] text-black px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatLaunchDate(item.launch_datetime) || "PRONTO"}
                        </span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold text-white">{item.title}</h2>
                      <p className="text-[#D946EF] mt-1 font-medium">{item.artist_name}</p>
                      <a
                        href={item.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Ver Video
                      </a>
                    </div>
                  </>
                ) : (
                  // Regular Artist Slide (or carousel-artist) with date or "PRONTO" badge
                  <>
                    {/* Blurred background fill so the full photo shows centered without cropping */}
                    <div className="absolute inset-0 bg-[#0a0a0a]" />
                    <img
                      src={item.photo_url ? `${BACKEND_URL}${item.photo_url}` : defaultImages[index % defaultImages.length]}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-40 scale-110"
                    />
                    <img
                      src={item.photo_url ? `${BACKEND_URL}${item.photo_url}` : defaultImages[index % defaultImages.length]}
                      alt={item.name}
                      className="absolute inset-0 w-full h-full object-contain"
                      onError={(e) => { e.target.src = defaultImages[index % defaultImages.length]; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                      {/* Date/Time badge for artist - shows date if available, otherwise PRONTO */}
                      <span className="inline-flex items-center gap-1 bg-[#FFD700] text-black px-3 py-1 rounded-full text-xs font-bold mb-2">
                        <Clock className="w-3 h-3" />
                        {formatLaunchDate(item.next_launch_datetime || item.launch_datetime) || "PRONTO"}
                      </span>
                      <h2 className="text-4xl md:text-5xl font-bold gradient-text">{item.name || item.title}</h2>
                      <p className="text-gray-300 mt-2 text-sm md:text-base">{item.bio || item.subtitle || "Artista LEYBA PRO"}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full h-full relative">
            <img
              src={defaultImages[0]}
              alt="E1 Entertainment"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
              <h2 className="text-4xl md:text-5xl font-bold gradient-text">E1 Entertainment</h2>
              <p className="text-gray-300 mt-2">Descubre artistas y gana viendo videos</p>
            </div>
          </div>
        )}

        {carouselItems.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              data-testid="carousel-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              data-testid="carousel-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Carousel Indicators */}
        {carouselItems.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {carouselItems.map((item, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide 
                    ? item.type === 'launch' ? "w-6 bg-[#FF6B6B]" : "w-6 bg-[#D946EF]"
                    : "bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
          </div>
        </div>
      </section>

      <div className="px-4 space-y-6 mt-6">
        {/* Announcements Section - RIGHT AFTER CAROUSEL */}
        <section data-testid="announcements-section" className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-[#D946EF]/20 to-[#8B5CF6]/20 rounded-lg">
              <Megaphone className="w-5 h-5 text-[#D946EF]" />
            </div>
            <h3 className="text-xl font-bold text-white">Anuncios</h3>
          </div>
          
          {announcements.length > 0 ? (
            <div className="space-y-4">
              {announcements.map((ann) => (
                <Card 
                  key={ann.id} 
                  className="bg-[#121212]/80 border border-white/5 overflow-hidden hover:border-[#D946EF]/30 transition-all duration-300 backdrop-blur-sm"
                  data-testid={`announcement-${ann.id}`}
                >
                  <CardContent className="p-0">
                    {ann.image_url && (
                      <div className="relative">
                        <img 
                          src={ann.image_url} 
                          alt={ann.title}
                          className="w-full h-36 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                      </div>
                    )}
                    <div className="p-4">
                      <h4 className="font-bold text-lg text-white">{ann.title}</h4>
                      <p className="text-sm text-gray-400 mt-1 leading-relaxed">{ann.content}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-[#121212]/60 border border-white/5 backdrop-blur-sm max-w-md mx-auto">
              <CardContent className="p-8 text-center">
                <Megaphone className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No hay anuncios disponibles</p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* 1 Promo Slide - After Announcements */}
        {promoSlidesTop.length > 0 && (
          <section data-testid="promo-top-section">
            <div className="max-w-md mx-auto">
              {promoSlidesTop.map(slide => (
                <PromoSlideCard key={slide.id} slide={slide} />
              ))}
            </div>
          </section>
        )}

        {/* Gallery Section - After first promo */}
        {galleryArtworks.length > 0 && (
          <section data-testid="gallery-section" className="max-w-md mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-gradient-to-br from-[#FFD700]/20 to-[#B8860B]/20 rounded-lg">
                <Image className="w-5 h-5 text-[#FFD700]" />
              </div>
              <h2 className="text-xl font-bold text-white">Galería de Artistas</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {galleryArtworks.slice(0, 6).map((artwork) => (
                <Card 
                  key={artwork.id} 
                  className="bg-[#121212]/80 border border-[#FFD700]/10 overflow-hidden group cursor-pointer hover:border-[#FFD700]/40 transition-all duration-300"
                  onClick={() => setSelectedImage(artwork)}
                >
                  <div className="relative aspect-square">
                    <img 
                      src={`${BACKEND_URL}${artwork.image_url}`}
                      alt={artwork.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden absolute inset-0 bg-[#1E1E1E] items-center justify-center">
                      <Image className="w-8 h-8 text-gray-500" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 pointer-events-none">
                      <p className="text-sm font-bold text-white truncate">{artwork.title}</p>
                      {artwork.artist_name && (
                        <p className="text-xs text-[#FFD700] truncate">{artwork.artist_name}</p>
                      )}
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-100 scale-75 pointer-events-none">
                      <div className="p-2 bg-[#FFD700] rounded-full shadow-lg">
                        <ZoomIn className="w-4 h-4 text-black" />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            {galleryArtworks.length > 6 && (
              <p className="text-center text-sm text-[#B8860B] mt-4 font-medium">
                +{galleryArtworks.length - 6} imágenes más
              </p>
            )}
          </section>
        )}

        {/* 2 Promo Slides - After Gallery */}
        {promoSlidesBottom.length > 0 && (
          <section data-testid="promo-bottom-section">
            <div className="max-w-md mx-auto space-y-4">
              {promoSlidesBottom.map(slide => (
                <PromoSlideCard key={slide.id} slide={slide} />
              ))}
            </div>
          </section>
        )}

        {/* Promoted Launches List (if any) */}
        {promotedLaunches.length > 0 && (
          <section data-testid="promoted-launches-section" className="max-w-md mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-gradient-to-br from-[#FF6B6B]/20 to-[#FF8E53]/20 rounded-lg">
                <Rocket className="w-5 h-5 text-[#FF6B6B]" />
              </div>
              <h3 className="text-xl font-bold text-white">Lanzamientos Destacados</h3>
            </div>
            
            <div className="space-y-3">
              {promotedLaunches.map((launch) => (
                <Card 
                  key={launch.id} 
                  className="bg-gradient-to-r from-[#FF6B6B]/10 to-[#FF8E53]/10 border border-[#FF6B6B]/20 overflow-hidden hover:border-[#FF6B6B]/40 transition-all duration-300"
                >
                  <CardContent className="p-0">
                    <div className="flex">
                      <div className="relative">
                        <img 
                          src={launch.thumbnail_url || `https://img.youtube.com/vi/${getYouTubeId(launch.youtube_url)}/mqdefault.jpg`}
                          alt={launch.title}
                          className="w-32 h-24 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50" />
                      </div>
                      <div className="p-3 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold line-clamp-1 text-white">{launch.title}</h4>
                        </div>
                        <p className="text-sm text-[#FF6B6B] font-medium">{launch.artist_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-[#FFD700] flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatLaunchDate(launch.launch_datetime) || "PRONTO"}
                          </span>
                        </div>
                        <a
                          href={launch.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-xs text-white/70 hover:text-white transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Ver Video
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Image Lightbox Modal - Centered */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-[100] bg-black/98 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            {/* Close button */}
            <button 
              className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 hover:rotate-90 z-10"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {/* Image container - Centered */}
            <div 
              className="relative flex flex-col items-center justify-center max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={`${BACKEND_URL}${selectedImage.image_url}`}
                alt={selectedImage.title}
                className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-2xl"
              />
              {/* Info below image */}
              <div className="mt-4 text-center px-4">
                <h3 className="text-xl font-bold text-white">{selectedImage.title}</h3>
                {selectedImage.artist_name && (
                  <p className="text-[#FFD700] font-medium mt-1">{selectedImage.artist_name}</p>
                )}
                {selectedImage.description && (
                  <p className="text-gray-300 text-sm mt-2 max-w-md">{selectedImage.description}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Logo Section with Download - Premium */}
        <section className="py-10 border-t border-[#FFD700]/10">
          <div className="text-center">
            <p className="text-xs text-[#B8860B] tracking-[0.3em] mb-6 font-medium">NUESTRA MARCA</p>
            <LeybaProLogo size="lg" showDownload={false} isGlowing={!!launchingSoon} />
            {launchingSoon && (
              <div className="mt-4 text-center animate-bounce">
                <p className="text-sm text-[#FF6B6B] font-bold">
                  🚀 Lanzamiento en {launchingSoon.minutes_until_launch} min
                </p>
                <p className="text-xs text-gray-400">{launchingSoon.launch?.title}</p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Launch Alert Modal */}
      {launchAlert && (
        <div 
          className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeInUp"
          onClick={() => setLaunchAlert(null)}
        >
          <div 
            className="bg-gradient-to-br from-[#FF6B6B]/20 to-[#DC2626]/20 border-2 border-[#FF6B6B] rounded-2xl p-6 max-w-sm w-full shadow-2xl shadow-[#FF6B6B]/50 animate-pulse"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#FF6B6B] to-[#DC2626] rounded-full mx-auto mb-4 flex items-center justify-center animate-bounce">
                <Rocket className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">🚀 ¡LANZAMIENTO!</h3>
              <h4 className="text-lg font-bold text-[#FF6B6B] mb-1">{launchAlert.title}</h4>
              <p className="text-[#FFD700] font-medium mb-4">{launchAlert.artist}</p>
              <p className="text-gray-300 text-sm mb-6">¡El lanzamiento está comenzando ahora!</p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setLaunchAlert(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 rounded-xl transition-colors"
                >
                  Cerrar
                </button>
                <a
                  href={launchAlert.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-gradient-to-r from-[#FF6B6B] to-[#DC2626] text-white font-bold py-3 rounded-xl hover:opacity-90 transition-opacity text-center flex items-center justify-center gap-2"
                  onClick={() => setLaunchAlert(null)}
                >
                  <ExternalLink className="w-4 h-4" />
                  Ver Ahora
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Install Instructions Modal */}
      {showInstallInstructions && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowInstallInstructions(false)}
        >
          <div 
            className="bg-[#1a1a1a] rounded-2xl p-6 max-w-sm w-full border border-[#FFD700]/30 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FFD700] to-[#B8860B] rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <Download className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white">Instalar LEYBA PRO</h3>
            </div>

            {isIOS ? (
              <div className="space-y-4">
                <p className="text-gray-300 text-sm text-center">Para instalar en iPhone/iPad:</p>
                <div className="bg-[#121212] rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">1</span>
                    <p className="text-white text-sm">Toca el botón <span className="text-[#FFD700] font-bold">Compartir</span> ↑</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">2</span>
                    <p className="text-white text-sm">Selecciona <span className="text-[#FFD700] font-bold">"Añadir a inicio"</span></p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">3</span>
                    <p className="text-white text-sm">Toca <span className="text-[#FFD700] font-bold">"Añadir"</span></p>
                  </div>
                </div>
              </div>
            ) : isAndroid ? (
              <div className="space-y-4">
                <p className="text-gray-300 text-sm text-center">Para instalar en Android:</p>
                <div className="bg-[#121212] rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">1</span>
                    <p className="text-white text-sm">Toca el menú <span className="text-[#FFD700] font-bold">⋮</span> (tres puntos)</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">2</span>
                    <p className="text-white text-sm">Selecciona <span className="text-[#FFD700] font-bold">"Instalar app"</span></p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">3</span>
                    <p className="text-white text-sm">Confirma la instalación</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-300 text-sm text-center">Para instalar:</p>
                <div className="bg-[#121212] rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">1</span>
                    <p className="text-white text-sm">Abre en <span className="text-[#FFD700] font-bold">Chrome</span> o <span className="text-[#FFD700] font-bold">Safari</span></p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center text-black font-bold">2</span>
                    <p className="text-white text-sm">Busca la opción <span className="text-[#FFD700] font-bold">"Instalar"</span> en el menú</p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowInstallInstructions(false)}
              className="w-full mt-6 bg-gradient-to-r from-[#FFD700] to-[#B8860B] text-black font-bold py-3 rounded-xl hover:opacity-90 transition-opacity"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
